import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Checkbox,
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
} from "@mui/material";
import {
    Delete as DeleteIcon,
    Add as AddIcon,
    Remove as RemoveIcon,
} from "@mui/icons-material";
import { useState, useEffect } from "react";
import { SectionConfig } from "./TaxReturnWebforms/types/formConfig";
import Button from "../atoms/Button";

interface SectionEditDialogProps {
    section: SectionConfig | null;
    open: boolean;
    onClose: () => void;
    onSave: (updated: SectionConfig) => void;
    onDelete?: () => void;
    canDelete: boolean;
}

const SectionEditDialog = ({
    section,
    open,
    onClose,
    onSave,
    onDelete,
    canDelete,
}: SectionEditDialogProps) => {
    const [title, setTitle] = useState<string>("");
    const [showDivider, setShowDivider] = useState<boolean>(false);
    const [layout, setLayout] = useState<'default' | 'columnar-properties'>('default');
    const [columnLabels, setColumnLabels] = useState<string[]>([]);

    useEffect(() => {
        if (section) {
            setTitle(section.title || "");
            setShowDivider(section.showDivider || false);
            setLayout(section.layout || 'default');
            setColumnLabels((section.columnLabels || []).map((c) => (typeof c === 'string' ? c : c.label)));
        }
    }, [section]);

    const handleSave = () => {
        if (!section) return;

        const updated: SectionConfig = {
            ...section,
            title,
            showDivider: showDivider || undefined,
            layout: layout === 'default' ? undefined : layout,
            columnLabels: layout === 'columnar-properties' && columnLabels.length > 0
                ? columnLabels
                : undefined,
        };

        // Clean up undefined values
        if (!updated.showDivider) delete updated.showDivider;
        if (!updated.layout) delete updated.layout;
        if (!updated.columnLabels || updated.columnLabels.length === 0) delete updated.columnLabels;

        onSave(updated);
        onClose();
    };

    const handleDelete = () => {
        if (onDelete) {
            onDelete();
            onClose();
        }
    };

    const handleAddColumnLabel = () => {
        setColumnLabels([...columnLabels, '']);
    };

    const handleRemoveColumnLabel = (index: number) => {
        const newLabels = columnLabels.filter((_, i) => i !== index);
        setColumnLabels(newLabels);
    };

    const handleColumnLabelChange = (index: number, value: string) => {
        const newLabels = [...columnLabels];
        newLabels[index] = value;
        setColumnLabels(newLabels);
    };

    if (!section) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Edit Section
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <TextField
                        label="Title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        fullWidth
                        size="small"
                        required
                    />

                    <FormControlLabel
                        control={(
                            <Checkbox
                                checked={showDivider}
                                onChange={(e) => setShowDivider(e.target.checked)}
                            />
                        )}
                        label={(
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography>Show divider</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    (adds a horizontal line above the section)
                                </Typography>
                            </Box>
                        )}
                    />

                    <FormControl fullWidth size="small">
                        <InputLabel>Layout</InputLabel>
                        <Select
                            value={layout}
                            label="Layout"
                            onChange={(e) => setLayout(e.target.value as 'default' | 'columnar-properties')}
                        >
                            <MenuItem value="default">Default</MenuItem>
                            <MenuItem value="columnar-properties">Columnar Properties</MenuItem>
                        </Select>
                    </FormControl>

                    {layout === 'columnar-properties' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="body2" fontWeight="medium">
                                    Column Labels
                                </Typography>
                                <Button
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={handleAddColumnLabel}
                                >
                                    Add Column
                                </Button>
                            </Box>
                            {columnLabels.length === 0 && (
                                <Typography variant="caption" color="text.secondary">
                                    No column labels defined. Add labels for the table header.
                                </Typography>
                            )}
                            {columnLabels.map((label, index) => (
                                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <TextField
                                        value={label}
                                        onChange={(e) => handleColumnLabelChange(index, e.target.value)}
                                        size="small"
                                        fullWidth
                                        placeholder={`Column ${index + 1}`}
                                    />
                                    <IconButton
                                        size="small"
                                        onClick={() => handleRemoveColumnLabel(index)}
                                        color="error"
                                    >
                                        <RemoveIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
                <Box>
                    {canDelete && onDelete && (
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={handleDelete}
                        >
                            Delete Section
                        </Button>
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSave} disabled={!title.trim()}>
                        Save
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default SectionEditDialog;
